import { Box } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import type { Excursion, ExcursionTemplateLimitsItem } from "api/excursions/Excursion.typed";
import { ExcursionStatusCancelled } from "api/excursions/Excursion.typed";
import { getExcursionStatusEmoji } from "../../shared/utils/planningUtils";
import {
  ExcursionCountWithMaxChip,
  ExcursionTemplateLimitsChips,
} from "../../../shared/excursionModal/display/ExcursionCountWithMaxChip";
import {
  buildExcursionTemplateLimitChipModel,
  excursionTimeRangeLabel,
} from "./excursionCardUtils";
import { excursionTooltipSublineColor, textTokens } from "styles/globalStyles";

export type ExcursionCardContentLayout = "month" | "slot" | "agenda";

export type ExcursionCardContentProps = {
  excursion: Excursion;
  layout: ExcursionCardContentLayout;
  /** List of excursion templates to resolve max people/groups when params_template has only id. */
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
};

export default function ExcursionCardContent({
  excursion: ex,
  layout,
  excursionTemplatesList,
}: ExcursionCardContentProps) {
  const statusEmoji = getExcursionStatusEmoji(ex.status);
  const limitModel = buildExcursionTemplateLimitChipModel(ex, excursionTemplatesList);
  const {
    totalPeople,
    groupCount,
    maxPeople: maxPeopleExcursion,
    maxGroups: maxGroupsExcursion,
  } = limitModel;
  const motifLine =
    ex.status === ExcursionStatusCancelled && ex.cancellation_reason?.trim()
      ? ex.cancellation_reason.trim()
      : "";
  const showExcursionLimits = true;

  if (layout === "agenda") {
    const timeLine = excursionTimeRangeLabel(ex);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          gap: 0.25,
        }}
      >
        <DarkTypography
          level="body-xs"
          sx={{
            color: textTokens.color.primary,
            fontWeight: 700,
            fontSize: "0.7rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          {statusEmoji} {ex.name}
        </DarkTypography>
        <DarkTypography
          level="body-xs"
          sx={{
            color: excursionTooltipSublineColor,
            fontSize: "0.65rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          {timeLine}
        </DarkTypography>
        {motifLine !== "" && (
          <DarkTypography
            level="body-xs"
            sx={{
              color: excursionTooltipSublineColor,
              fontSize: "0.6rem",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {motifLine}
          </DarkTypography>
        )}
        {showExcursionLimits && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: "auto",
              pt: 0.15,
              flexShrink: 0,
            }}
          >
            <ExcursionCountWithMaxChip
              type="totalPeople"
              count={totalPeople}
              max={maxPeopleExcursion}
              size="xs"
            />
          </Box>
        )}
      </Box>
    );
  }

  if (layout === "month") {
    return (
      <>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", minWidth: 0 }}
        >
          <DarkTypography
            level="body-xs"
            sx={{
              color: textTokens.color.primary,
              fontWeight: 700,
              fontSize: { xs: "0.8rem", sm: "0.75rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            {statusEmoji} {ex.name} {ex.start_time && `• ${ex.start_time}`}
          </DarkTypography>
          {showExcursionLimits && (
            <Box sx={{ flexShrink: 0, display: "flex", gap: 0.5, alignItems: "center" }}>
              <ExcursionTemplateLimitsChips
                totalPeople={totalPeople}
                groupCount={groupCount}
                maxPeople={maxPeopleExcursion}
                maxGroups={maxGroupsExcursion}
                size="xs"
              />
            </Box>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        gap: 0.25,
      }}
    >
      <DarkTypography
        level="body-xs"
        sx={{
          color: textTokens.color.primary,
          fontWeight: 700,
          fontSize: "0.7rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {statusEmoji} {ex.name}
      </DarkTypography>
      {showExcursionLimits && (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: "auto", pt: 0.15, flexShrink: 0 }}
        >
          <ExcursionCountWithMaxChip
            type="totalPeople"
            count={totalPeople}
            max={maxPeopleExcursion}
            size="xs"
          />
        </Box>
      )}
    </Box>
  );
}
